import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Auth/Login';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import UserList from './components/Users/UserList';
import JobList from './components/Jobs/JobList';
import AdminCVList from './components/CV/AdminCVList';
import AdminCVTemplates from './components/CV/AdminCVTemplates';
import AdminTaxonomy from './components/Taxonomy/AdminTaxonomy';
import ServicePriceManagement from './components/Services/ServicePriceManagement';
import DiscountCodeManagement from './components/DiscountCodes/DiscountCodeManagement';
import OrderList from './components/Orders/OrderList';
import CompanyList from './components/Companies/CompanyList';
import PendingApproval from './components/PendingApproval/PendingApproval';
import PasswordGate from './components/PasswordGate';
import './App.scss';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    // Verify the stored token against the backend instead of trusting its mere
    // presence. A stale/expired/non-admin token used to load an empty admin
    // shell (requiring a manual logout/login); now we fail closed.
    const verifySession = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch('/api/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => null);
        if (res.ok && data?.success && data?.data?.user?.user_type === 'admin') {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          setIsAuthenticated(false);
        }
      } catch {
        // Network/parse failure → don't grant access.
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    verifySession();
  }, []);

  const handleLogin = (token: string) => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setIsAuthenticated(false);
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner">Kraunama...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <PasswordGate>
        <Login onLogin={handleLogin} />
      </PasswordGate>
    );
  }

  return (
    <PasswordGate>
    <Router>
      <div className="App">
        <Sidebar collapsed={sidebarCollapsed} />
        <Header 
          onToggleSidebar={handleToggleSidebar}
          sidebarCollapsed={sidebarCollapsed}
          onLogout={handleLogout}
        />
        
        <main className={`App-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Routes>
            <Route path="/" element={<Navigate to="/pending" replace />} />
            <Route path="/pending" element={<PendingApproval />} />
            <Route path="/users" element={<UserList />} />
            <Route path="/jobs" element={<JobList />} />
            <Route path="/companies" element={<CompanyList />} />
            <Route path="/applications" element={<div className="page-placeholder">Aplikacijos - netrukus</div>} />
            <Route path="/cvs" element={<AdminCVList />} />
            <Route path="/cv-templates" element={<AdminCVTemplates />} />
            <Route path="/taxonomy" element={<AdminTaxonomy />} />
            <Route path="/services" element={<ServicePriceManagement />} />
            <Route path="/discount-codes" element={<DiscountCodeManagement />} />
            <Route path="/orders" element={<OrderList />} />
            <Route path="/analytics" element={<div className="page-placeholder">Analitika - netrukus</div>} />
            <Route path="/settings" element={<div className="page-placeholder">Nustatymai - netrukus</div>} />
            <Route path="*" element={<div className="not-found">Puslapis nerastas</div>} />
          </Routes>
        </main>
      </div>
    </Router>
    </PasswordGate>
  );
}

export default App;
